// Weather Center (WEB-7144): which place the forecast is for, shared by the map's weather pill
// and the Weather Center panel, plus the barometer unit. Both are persisted (redux-persist), so
// the place a user chose comes back after a reload, like mobile's WeatherCacheManager.
//
// location: { kind: 'search' | 'mine' | 'center', label, sub, lat, lng, chosen }
// `chosen` is false for a default the app picked; a default is worked out again on the next
// load rather than remembered.

import weatherCenterApi from '../lib/api/weatherCenter'
import { placeLabel } from '../lib/weatherCenter'

export const WEATHER_CENTER_SET_LOCATION = 'WEATHER_CENTER_SET_LOCATION'
export const WEATHER_CENTER_SET_BAROMETER = 'WEATHER_CENTER_SET_BAROMETER'

export const setWeatherLocation = location => ({
  type: WEATHER_CENTER_SET_LOCATION,
  payload: location
})

export const setWeatherBarometer = unit => ({
  type: WEATHER_CENTER_SET_BAROMETER,
  payload: unit
})

const round4 = v => Math.round(v * 10000) / 10000

// The middle of whichever map is showing: Google's LatLng in 2D, MapLibre's LngLat in 3D.
export const currentMapCenter = state => {
  const map3d = state.is3dMapselected ? state.mapReferenceThreed : null
  const center = map3d && map3d.getCenter ? map3d.getCenter() : null
  if (center) return { lat: center.lat, lng: center.lng }
  const map = typeof state.mapReference === 'function' ? state.mapReference() : null
  const latLng = map && map.getCenter ? map.getCenter() : null
  return latLng ? { lat: latLng.lat(), lng: latLng.lng() } : null
}

export const mapCenterLocation = ({ lat, lng }, chosen) => ({
  kind: 'center',
  label: 'Map center',
  sub: `${round4(lat).toFixed(4)}, ${round4(lng).toFixed(4)}`,
  lat: round4(lat),
  lng: round4(lng),
  chosen
})

export const myLocation = (coords, chosen) => {
  const miles = coords.accuracy ? coords.accuracy / 1609.344 : null
  const accuracy = miles == null ? '' : miles < 0.1 ? ' · within 500 ft' : ` · within about ${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`
  return {
    kind: 'mine',
    label: 'My location',
    sub: `From your browser${accuracy}`,
    lat: round4(coords.latitude),
    lng: round4(coords.longitude),
    chosen
  }
}

// 'granted' | 'denied' | 'prompt', or null where the Permissions API can't say.
export const geolocationPermission = () => {
  if (!navigator.permissions || !navigator.permissions.query) return Promise.resolve(null)
  return navigator.permissions.query({ name: 'geolocation' })
    .then(status => status.state)
    .catch(() => null)
}

export const currentPosition = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('unsupported'))
    return
  }
  navigator.geolocation.getCurrentPosition(
    position => resolve(position.coords),
    reject,
    { enableHighAccuracy: false, timeout: 15000, maximumAge: 10 * 60 * 1000 }
  )
})

/**
 * Settle the forecast location without ever prompting for permission:
 *   1. a place the user chose (kept; "My location" is refreshed if permission still stands),
 *   2. else the browser's location, only if permission is already granted,
 *   3. else the middle of the map.
 * Resolves the location it settled on, or null if the map isn't ready yet.
 */
export const resolveWeatherLocation = () => (dispatch, getState) => {
  const { location } = getState().weatherCenter || {}

  // A map center chosen before it could be named (or before names existed) gets one now.
  if (location && location.chosen && location.kind !== 'mine') return dispatch(nameMapCenter(location))

  return geolocationPermission().then(permission => {
    if (permission === 'granted') {
      return currentPosition()
        .then(coords => {
          const mine = myLocation(coords, !!(location && location.chosen))
          dispatch(setWeatherLocation(mine))
          return mine
        })
        .catch(() => fallBackToCenter(dispatch, getState, location))
    }
    return fallBackToCenter(dispatch, getState, location)
  })
}

// The map center is shown by the name of the place it's in ("Jennings, KS", "Near Wilsall, MT"),
// with "Map center" and its coordinates underneath. It reads "Map center" until the name
// arrives, and stays that way if there isn't one. The name only lands if the place hasn't
// changed in the meantime.
export const nameMapCenter = location => (dispatch, getState) => {
  if (!location || location.kind !== 'center' || location.label !== 'Map center') return Promise.resolve(location)
  return weatherCenterApi.reverseGeocode({ lat: location.lat, lng: location.lng })
    .then(results => {
      const name = placeLabel(results)
      const current = (getState().weatherCenter || {}).location
      if (!name || !current || current.lat !== location.lat || current.lng !== location.lng) return current
      const named = { ...current, label: name, sub: `Map center · ${location.sub}` }
      dispatch(setWeatherLocation(named))
      return named
    })
    .catch(() => location)
}

// "Use map center": the middle of the map right now. It doesn't follow later panning.
export const chooseMapCenter = () => (dispatch, getState) => {
  const center = currentMapCenter(getState())
  if (!center) return null
  const location = mapCenterLocation(center, true)
  dispatch(setWeatherLocation(location))
  dispatch(nameMapCenter(location))
  return location
}

const fallBackToCenter = (dispatch, getState, location) => {
  if (location && location.chosen) return location
  const center = currentMapCenter(getState())
  if (!center) return null
  const settled = mapCenterLocation(center, false)
  dispatch(setWeatherLocation(settled))
  dispatch(nameMapCenter(settled))
  return settled
}
